import * as d3 from 'd3';
import { GroupHistogramData, OriginalDataItem } from '../../../types/histogram.types';
import { D3SelectionHook } from '../../hooks/useD3Selection';
import { D3TooltipHook } from '../../hooks/useD3Tooltip';
import { createDefaultCategoricalScale } from '../../constants/palette-utils';

interface RenderBarsOptions {
  chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
  histData?: d3.Bin<number, number>[];
  groupHistData?: GroupHistogramData[];
  isGrouped: boolean;
  groupMode: 'overlay' | 'stacked' | 'faceted';
  orientation: 'vertical' | 'horizontal';
  currentXScale: d3.ScaleLinear<number, number>;
  currentYScale: d3.ScaleLinear<number, number>;
  xScale: d3.ScaleLinear<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  innerHeight: number;
  selection: D3SelectionHook;
  tooltip: D3TooltipHook;
  onBinClick?: (minValue: number, maxValue: number, dataIndices: number[], isAdditive?: boolean) => void;
  originalData?: OriginalDataItem[];
  allData: number[];
}

/**
 * グループ化されたヒストグラムのバーを描画
 */
export const renderGroupedBars = ({
  chartGroup,
  groupHistData,
  groupMode,
  orientation,
  currentXScale,
  currentYScale,
  xScale,
  yScale,
  innerHeight,
  selection,
  tooltip,
  onBinClick
}: RenderBarsOptions & { groupHistData: GroupHistogramData[] }): void => {
  const colorScale = createDefaultCategoricalScale();

  // 既存のバーをクリア
  chartGroup.selectAll('.bar-all').remove();
  chartGroup.selectAll('.bar-selected').remove();
  chartGroup.selectAll('[class^="bar-group-"]').remove();
  chartGroup.selectAll('[class^="selection-outline-"]').remove();
  chartGroup.selectAll('[class^="selection-partial-"]').remove();

  // グループごとに処理
  groupHistData.forEach((group, groupIndex) => {
    const groupColor = colorScale(group.groupName);

    // グループのバーを描画
    const groupBars = chartGroup.selectAll<SVGRectElement, d3.Bin<number, number>>(`.bar-group-${groupIndex}`)
      .data(group.histogram, (d: d3.Bin<number, number>) => `${group.groupName}-${d.x0}-${d.x1}`);

    groupBars.exit().remove();

    const groupEnter = groupBars.enter()
      .append('rect')
      .attr('class', `bar-group-${groupIndex}`)
      .attr('fill', groupColor)
      .style('cursor', onBinClick ? 'pointer' : 'default');

    // 位置とサイズの計算
    groupBars.merge(groupEnter)
      .style('opacity', function() {
        const baseOpacity = groupMode === 'overlay' ? 0.6 : 0.8;
        return selection.hasSelection ? 0.3 : baseOpacity;
      })
      .attr('x', (d: d3.Bin<number, number>) => {
        if (orientation === 'horizontal') {
          if (groupMode === 'stacked') {
            let baseWidth = 0;
            for (let i = 0; i < groupIndex; i++) {
              const prevGroup = groupHistData[i];
              const prevBin = prevGroup.histogram.find((b) =>
                Math.abs((b.x0 || 0) - (d.x0 || 0)) < 0.0001 &&
                Math.abs((b.x1 || 0) - (d.x1 || 0)) < 0.0001
              );
              if (prevBin) {
                baseWidth += prevBin.length;
              }
            }
            return xScale(baseWidth);
          }
          return 0;
        } else {
          return currentXScale(d.x0 || 0);
        }
      })
      .attr('y', (d: d3.Bin<number, number>) => {
        if (orientation === 'horizontal') {
          return currentYScale(d.x1 || 0);
        } else {
          if (groupMode === 'stacked') {
            let baseHeight = 0;
            for (let i = 0; i < groupIndex; i++) {
              const prevGroup = groupHistData[i];
              const prevBin = prevGroup.histogram.find((b) =>
                Math.abs((b.x0 || 0) - (d.x0 || 0)) < 0.0001 &&
                Math.abs((b.x1 || 0) - (d.x1 || 0)) < 0.0001
              );
              if (prevBin) {
                baseHeight += prevBin.length;
              }
            }
            return yScale(baseHeight + d.length);
          }
          return yScale(d.length);
        }
      })
      .attr('width', (d: d3.Bin<number, number>) => {
        if (orientation === 'horizontal') {
          if (groupMode === 'stacked') {
            return Math.max(0, xScale(d.length) - xScale(0));
          }
          return xScale(d.length);
        } else {
          return Math.max(0, currentXScale(d.x1 || 0) - currentXScale(d.x0 || 0) - 1);
        }
      })
      .attr('height', (d: d3.Bin<number, number>) => {
        if (orientation === 'horizontal') {
          return Math.max(0, currentYScale(d.x0 || 0) - currentYScale(d.x1 || 0));
        } else {
          if (groupMode === 'stacked') {
            return Math.max(0, innerHeight - yScale(d.length) - (innerHeight - yScale(0)));
          }
          return innerHeight - yScale(d.length);
        }
      })
      .on('mouseover', function(this: SVGRectElement, event: MouseEvent, d: d3.Bin<number, number>) {
        let selectedCount = 0;
        if (selection.hasSelection && group.originalData) {
          const minValue = d.x0 || 0;
          const maxValue = d.x1 || 0;
          const isLastBin = maxValue === Math.max(...group.originalData.map((item) => item.value));
          const binData = group.originalData
            .filter((item) => {
              if (isLastBin) {
                return item.value >= minValue && item.value <= maxValue;
              } else {
                return item.value >= minValue && item.value < maxValue;
              }
            });
          selectedCount = binData.filter(item => selection.isSelected(item.index)).length;
        }

        const selectionInfo = selection.hasSelection && selectedCount > 0
          ? `<div style="color: #ff6b6b;">選択: ${selectedCount} / ${d.length} (${((selectedCount / d.length) * 100).toFixed(1)}%)</div>`
          : '';

        tooltip.show(
          `<div><strong>${group.groupName}</strong></div>
            <div>範囲: ${(d.x0 || 0).toFixed(2)} - ${(d.x1 || 0).toFixed(2)}</div>
            <div>度数: ${d.length}</div>
            <div>グループ内割合: ${((d.length / group.histogram.reduce((sum: number, b) => sum + b.length, 0)) * 100).toFixed(1)}%</div>
            ${selectionInfo}`,
          event
        );
      })
      .on('mousemove', function(event: MouseEvent) {
        tooltip.move(event);
      })
      .on('mouseout', function() {
        tooltip.hide();
      })
      .on('click', function(this: SVGRectElement, event: MouseEvent, d: d3.Bin<number, number>) {
        if (onBinClick && group.originalData) {
          const minValue = d.x0 || 0;
          const maxValue = d.x1 || 0;

          const isLastBin = maxValue === Math.max(...group.originalData.map((item) => item.value));
          const dataIndices = group.originalData
            .filter((item) => {
              if (isLastBin) {
                return item.value >= minValue && item.value <= maxValue;
              } else {
                return item.value >= minValue && item.value < maxValue;
              }
            })
            .map((item) => item.index);

          const isAdditive = event.ctrlKey || event.metaKey;
          onBinClick(minValue, maxValue, dataIndices, isAdditive);
        }
      });
  });
};

/**
 * グループ化されたヒストグラムの選択バーを描画
 */
export const renderGroupedSelectionBars = ({
  chartGroup,
  groupHistData,
  groupMode,
  orientation,
  currentXScale,
  currentYScale,
  xScale,
  yScale,
  innerHeight,
  selection,
  onBinClick
}: RenderBarsOptions & { groupHistData: GroupHistogramData[] }): void => {
  if (!selection.hasSelection) return;

  const colorScale = d3.scaleOrdinal(d3.schemeCategory10);

  groupHistData.forEach((group, groupIndex) => {
    const groupColor = colorScale(group.groupName);

    const selectedGroupBars = chartGroup.selectAll<SVGRectElement, d3.Bin<number, number>>(
      `.selected-bar-group-${groupIndex}`
    ).data(
      group.histogram.filter(d => {
        if (!group.originalData) return false;
        const minValue = d.x0 || 0;
        const maxValue = d.x1 || 0;
        const isLastBin = maxValue === Math.max(...group.originalData.map((item) => item.value));
        const binDataIndices = group.originalData
          .filter((item) => {
            if (isLastBin) {
              return item.value >= minValue && item.value <= maxValue;
            } else {
              return item.value >= minValue && item.value < maxValue;
            }
          })
          .map((item) => item.index);

        return binDataIndices.some(idx => selection.isSelected(idx));
      }),
      (d: d3.Bin<number, number>) => `selected-${group.groupName}-${d.x0}-${d.x1}`
    );

    selectedGroupBars.exit().remove();

    const selectedEnter = selectedGroupBars.enter()
      .append('rect')
      .attr('class', `selected-bar-group-${groupIndex}`)
      .attr('fill', groupColor)
      .style('cursor', onBinClick ? 'pointer' : 'default')
      .style('pointer-events', 'none');

    selectedGroupBars.merge(selectedEnter)
      .style('opacity', groupMode === 'overlay' ? 0.6 : 0.8)
      .attr('x', (d: d3.Bin<number, number>) => {
        if (orientation === 'horizontal') {
          if (groupMode === 'stacked') {
            let baseWidth = 0;
            for (let i = 0; i < groupIndex; i++) {
              const prevGroup = groupHistData[i];
              const prevBin = prevGroup.histogram.find((b) =>
                Math.abs((b.x0 || 0) - (d.x0 || 0)) < 0.0001 &&
                Math.abs((b.x1 || 0) - (d.x1 || 0)) < 0.0001
              );
              if (prevBin) {
                baseWidth += prevBin.length;
              }
            }
            return xScale(baseWidth);
          }
          return 0;
        } else {
          return currentXScale(d.x0 || 0);
        }
      })
      .attr('y', (d: d3.Bin<number, number>) => {
        if (orientation === 'horizontal') {
          return currentYScale(d.x1 || 0);
        } else {
          const minValue = d.x0 || 0;
          const maxValue = d.x1 || 0;
          const isLastBin = maxValue === Math.max(...group.originalData.map((item) => item.value));
          const binData = group.originalData
            .filter((item) => {
              if (isLastBin) {
                return item.value >= minValue && item.value <= maxValue;
              } else {
                return item.value >= minValue && item.value < maxValue;
              }
            });
          const selectedCount = binData.filter(item => selection.isSelected(item.index)).length;

          if (groupMode === 'stacked') {
            let baseHeight = 0;
            for (let i = 0; i < groupIndex; i++) {
              const prevGroup = groupHistData[i];
              const prevBin = prevGroup.histogram.find((b) =>
                Math.abs((b.x0 || 0) - (d.x0 || 0)) < 0.0001 &&
                Math.abs((b.x1 || 0) - (d.x1 || 0)) < 0.0001
              );
              if (prevBin) {
                const prevMinValue = prevBin.x0 || 0;
                const prevMaxValue = prevBin.x1 || 0;
                const prevIsLastBin = prevMaxValue === Math.max(...prevGroup.originalData.map((item) => item.value));
                const prevBinData = prevGroup.originalData
                  .filter((item) => {
                    if (prevIsLastBin) {
                      return item.value >= prevMinValue && item.value <= prevMaxValue;
                    } else {
                      return item.value >= prevMinValue && item.value < prevMaxValue;
                    }
                  });
                const prevSelectedCount = prevBinData.filter(item => selection.isSelected(item.index)).length;
                baseHeight += prevSelectedCount;
              }
            }
            return yScale(baseHeight + selectedCount);
          }
          return yScale(selectedCount);
        }
      })
      .attr('width', (d: d3.Bin<number, number>) => {
        if (orientation === 'horizontal') {
          const minValue = d.x0 || 0;
          const maxValue = d.x1 || 0;
          const isLastBin = maxValue === Math.max(...group.originalData.map((item) => item.value));
          const binData = group.originalData
            .filter((item) => {
              if (isLastBin) {
                return item.value >= minValue && item.value <= maxValue;
              } else {
                return item.value >= minValue && item.value < maxValue;
              }
            });
          const selectedCount = binData.filter(item => selection.isSelected(item.index)).length;

          if (groupMode === 'stacked') {
            return Math.max(0, xScale(selectedCount) - xScale(0));
          }
          return xScale(selectedCount);
        } else {
          return Math.max(0, currentXScale(d.x1 || 0) - currentXScale(d.x0 || 0) - 1);
        }
      })
      .attr('height', (d: d3.Bin<number, number>) => {
        if (orientation === 'horizontal') {
          return Math.max(0, currentYScale(d.x0 || 0) - currentYScale(d.x1 || 0));
        } else {
          const minValue = d.x0 || 0;
          const maxValue = d.x1 || 0;
          const isLastBin = maxValue === Math.max(...group.originalData.map((item) => item.value));
          const binData = group.originalData
            .filter((item) => {
              if (isLastBin) {
                return item.value >= minValue && item.value <= maxValue;
              } else {
                return item.value >= minValue && item.value < maxValue;
              }
            });
          const selectedCount = binData.filter(item => selection.isSelected(item.index)).length;

          if (groupMode === 'stacked') {
            return Math.max(0, innerHeight - yScale(selectedCount) - (innerHeight - yScale(0)));
          }
          return innerHeight - yScale(selectedCount);
        }
      });
  });
};

/**
 * 非グループ化ヒストグラムのバーを描画
 */
export const renderUngroupedBars = ({
  chartGroup,
  histData,
  orientation,
  currentXScale,
  currentYScale,
  xScale,
  yScale,
  innerHeight,
  selection,
  tooltip,
  onBinClick,
  originalData,
  allData
}: RenderBarsOptions & { histData: d3.Bin<number, number>[] }): void => {
  // グループ化時の要素をクリア
  chartGroup.selectAll('[class^="bar-group-"]').remove();
  chartGroup.selectAll('[class^="selection-outline-"]').remove();
  chartGroup.selectAll('[class^="selection-partial-"]').remove();

  const allBars = chartGroup.selectAll<SVGRectElement, d3.Bin<number, number>>('.bar-all')
    .data(histData, d => `${d.x0}-${d.x1}`);

  allBars.exit().remove();

  const allEnter = allBars.enter()
    .append('rect')
    .attr('class', 'bar-all')
    .attr('fill', '#4f81bd')
    .attr('stroke', '#2c5282')
    .attr('stroke-width', 0.5)
    .style('opacity', selection.hasSelection ? selection.getOpacity(0) : 0.8)
    .style('cursor', onBinClick ? 'pointer' : 'default');

  allBars.merge(allEnter)
    .attr('x', d => orientation === 'horizontal' ? 0 : currentXScale(d.x0 || 0))
    .attr('y', d => orientation === 'horizontal' ? currentYScale(d.x1 || 0) : yScale(d.length))
    .attr('width', d => orientation === 'horizontal'
      ? xScale(d.length)
      : Math.max(0, currentXScale(d.x1 || 0) - currentXScale(d.x0 || 0) - 1))
    .attr('height', d => orientation === 'horizontal'
      ? Math.max(0, currentYScale(d.x0 || 0) - currentYScale(d.x1 || 0))
      : innerHeight - yScale(d.length))
    .style('opacity', selection.hasSelection ? 0.3 : 0.8)
    .on('mouseover', function(this: SVGRectElement, event: MouseEvent, d: d3.Bin<number, number>) {
      let binDataIndices: number[] = [];
      let selectedCount = 0;
      if (originalData) {
        const minValue = d.x0 || 0;
        const maxValue = d.x1 || 0;
        const isLastBin = maxValue === d3.max(originalData, item => item.value);
        const binData = originalData
          .filter(item => {
            if (isLastBin) {
              return item.value >= minValue && item.value <= maxValue;
            } else {
              return item.value >= minValue && item.value < maxValue;
            }
          });
        binDataIndices = binData.map(item => item.index);
        selectedCount = selection.hasSelection ? binData.filter(item => selection.isSelected(item.index)).length : 0;
      }

      const clickHint = onBinClick ? selection.getClickHint(binDataIndices) : '';
      const selectionInfo = selection.hasSelection && selectedCount > 0
        ? `<div style="color: #ff6b6b;">選択: ${selectedCount} / ${d.length} (${((selectedCount / d.length) * 100).toFixed(1)}%)</div>`
        : '';

      tooltip.show(
        `<div>範囲: ${(d.x0 || 0).toFixed(2)} - ${(d.x1 || 0).toFixed(2)}</div>
          <div>総度数: ${d.length}</div>
          <div>割合: ${((d.length / allData.length) * 100).toFixed(1)}%</div>
          ${selectionInfo}
          ${clickHint ? `<div style="margin-top: 4px; font-style: italic;">${clickHint}</div>` : ''}`,
        event
      );
    })
    .on('mousemove', function(event: MouseEvent) {
      tooltip.move(event);
    })
    .on('mouseout', function() {
      tooltip.hide();
    })
    .on('click', function(this: SVGRectElement, event: MouseEvent, d: d3.Bin<number, number>) {
      if (onBinClick && originalData) {
        const minValue = d.x0 || 0;
        const maxValue = d.x1 || 0;

        const isLastBin = maxValue === d3.max(originalData, item => item.value);
        const dataIndices = originalData
          .filter(item => {
            if (isLastBin) {
              return item.value >= minValue && item.value <= maxValue;
            } else {
              return item.value >= minValue && item.value < maxValue;
            }
          })
          .map(item => item.index);

        const isAdditive = event.ctrlKey || event.metaKey;

        onBinClick(minValue, maxValue, dataIndices, isAdditive);
      }
    });
};

/**
 * 非グループ化ヒストグラムの選択バーを描画
 */
export const renderUngroupedSelectionBars = ({
  chartGroup,
  histData,
  orientation,
  currentXScale,
  currentYScale,
  xScale,
  yScale,
  innerHeight,
  selection,
  onBinClick,
  originalData
}: RenderBarsOptions & { histData: d3.Bin<number, number>[] }): void => {
  chartGroup.selectAll('.bar-selected').remove();

  if (!selection.hasSelection || !originalData) return;

  const selectedBars = chartGroup.selectAll<SVGRectElement, d3.Bin<number, number>>('.bar-selected')
    .data(histData.filter(d => {
      const minValue = d.x0 || 0;
      const maxValue = d.x1 || 0;
      const isLastBin = maxValue === d3.max(originalData, item => item.value);
      const binDataIndices = originalData
        .filter(item => {
          if (isLastBin) {
            return item.value >= minValue && item.value <= maxValue;
          } else {
            return item.value >= minValue && item.value < maxValue;
          }
        })
        .map(item => item.index);

      return binDataIndices.some(idx => selection.isSelected(idx));
    }), d => `selected-${d.x0}-${d.x1}`);

  selectedBars.exit().remove();

  const selectedEnter = selectedBars.enter()
    .append('rect')
    .attr('class', 'bar-selected')
    .attr('fill', '#4f81bd')
    .attr('stroke', '#2c5282')
    .attr('stroke-width', 0.5)
    .style('opacity', 0.8)
    .style('cursor', onBinClick ? 'pointer' : 'default')
    .style('pointer-events', 'none');

  selectedBars.merge(selectedEnter)
    .attr('x', d => orientation === 'horizontal' ? 0 : currentXScale(d.x0 || 0))
    .attr('y', d => {
      if (orientation === 'horizontal') {
        return currentYScale(d.x1 || 0);
      } else {
        const minValue = d.x0 || 0;
        const maxValue = d.x1 || 0;
        const isLastBin = maxValue === d3.max(originalData, item => item.value);
        const binData = originalData
          .filter(item => {
            if (isLastBin) {
              return item.value >= minValue && item.value <= maxValue;
            } else {
              return item.value >= minValue && item.value < maxValue;
            }
          });

        const selectedCount = binData.filter(item => selection.isSelected(item.index)).length;
        return yScale(selectedCount);
      }
    })
    .attr('width', d => {
      if (orientation === 'horizontal') {
        const minValue = d.x0 || 0;
        const maxValue = d.x1 || 0;
        const isLastBin = maxValue === d3.max(originalData, item => item.value);
        const binData = originalData
          .filter(item => {
            if (isLastBin) {
              return item.value >= minValue && item.value <= maxValue;
            } else {
              return item.value >= minValue && item.value < maxValue;
            }
          });

        const selectedCount = binData.filter(item => selection.isSelected(item.index)).length;
        return xScale(selectedCount);
      } else {
        return Math.max(0, currentXScale(d.x1 || 0) - currentXScale(d.x0 || 0) - 1);
      }
    })
    .attr('height', d => {
      if (orientation === 'horizontal') {
        return Math.max(0, currentYScale(d.x0 || 0) - currentYScale(d.x1 || 0));
      } else {
        const minValue = d.x0 || 0;
        const maxValue = d.x1 || 0;
        const isLastBin = maxValue === d3.max(originalData, item => item.value);
        const binData = originalData
          .filter(item => {
            if (isLastBin) {
              return item.value >= minValue && item.value <= maxValue;
            } else {
              return item.value >= minValue && item.value < maxValue;
            }
          });

        const selectedCount = binData.filter(item => selection.isSelected(item.index)).length;
        return innerHeight - yScale(selectedCount);
      }
    });
};
